/**
 * Профиль фирмы: реквизиты и документы со статусом каждого.
 *
 * Реквизиты приходят **вложением** в профиль пользователя: операции «дай фирму
 * по идентификатору» в контракте нет вовсе, и второго запроса за ними быть не
 * может. Участия отбираются единственным объявленным помощником слоя
 * контракта — второго такого отбора трек не заводит.
 *
 * «Статус верификации» прочитан статусом **документа**, а не сводным признаком
 * фирмы: поля верификации у фирмы нет, а вывести его сложением статусов
 * документов значило бы завести правило «какие типы обязательны и что делает
 * просроченный», которого не называет ни одно требование. Поэтому сводного
 * «фирма верифицирована» на экране нет ни в каком виде.
 *
 * Значение статуса вне порождённого перечня показывается подписью «неизвестно»
 * — **без исходной строки**: машинное значение сервера человеку нечем
 * использовать.
 *
 * Состояние «пусто» принадлежит **списку документов**, а не экрану. Реквизиты
 * приходят вложением в профиль и есть у фирмы с первой её минуты, а документов
 * у новой фирмы нет по определению: назначив пустоту списка состоянием всего
 * экрана, кабинет прятал бы реквизиты ровно в том состоянии, в котором фирма
 * находится сразу после создания. Экран целиком остаётся за исходами, в
 * которых показывать нечего вовсе, — за незавершённым чтением и за отказом.
 */

import { ApiFailure } from '../api/client';
import type { ApiClient } from '../api/client';
import { membershipsIn } from '../api/contract';
import type { Company, Document } from '../api/contract';
import { companyTypeStringKey, documentStatusStringKey } from '../i18n';
import type { Strings } from '../i18n';
import { enumLabel, placeState, screenStateFor } from './state';
import type { ScreenState } from './state';

export interface CompanyProfileScreenOptions {
  readonly client: ApiClient;
  readonly strings: Strings;
  readonly companyId: string;
}

export interface CompanyProfileScreen {
  readonly element: HTMLElement;
  readonly state: ScreenState | null;
  readonly documents: readonly Document[];
  load(): Promise<void>;
}

export function createCompanyProfileScreen(
  options: CompanyProfileScreenOptions,
): CompanyProfileScreen {
  const { client, strings, companyId } = options;

  const element = document.createElement('section');
  const heading = document.createElement('h1');
  const requisites = document.createElement('p');
  const documentsHeading = document.createElement('h2');
  const list = document.createElement('ul');

  element.className = 'screen screen-company-profile';
  heading.textContent = strings.company_profile_title;
  documentsHeading.textContent = strings.company_documents_title;

  let state: ScreenState | null = 'loading';
  let company: Company | null = null;
  let documents: readonly Document[] = [];

  function row(entry: Document): HTMLElement {
    const node = document.createElement('li');

    node.className = 'document-row';
    // Статус каждого документа — подписью по ключу порождённого перечня.
    // Сводного статуса фирмы рядом нет: его не существует ни полем, ни
    // правилом, которое клиенту позволено было бы выполнить.
    node.textContent = [
      enumLabel(strings, documentStatusStringKey(entry.status)),
      entry.number ?? '',
      entry.expires_at ?? '',
    ].join(' ');

    return node;
  }

  function render(): void {
    requisites.textContent =
      company === null
        ? ''
        : [
            company.name,
            // Подпись типа фирмы — строителем по префиксу из порождённого
            // перечня, без рукописной таблицы (долг `TD-73`).
            enumLabel(strings, companyTypeStringKey(company.type)),
            company.tax_id,
            company.legal_address,
          ].join(' ');

    const rows: HTMLElement[] = [];

    for (const entry of documents) {
      rows.push(row(entry));
    }

    list.replaceChildren(...rows);
    // Правило места состояния — одна функция на все экраны (долг `TD-66`).
    // Ответ здесь — реквизиты: нет их — показывать нечего вовсе, и состояние
    // принадлежит экрану целиком; есть — состояние встаёт на место **списка**
    // документов, и реквизиты показаны и тогда, когда документов нет ни одного.
    placeState(
      element,
      { state, answered: company !== null, frame: [heading, requisites, documentsHeading], list },
      strings,
    );
  }

  async function load(): Promise<void> {
    state = screenStateFor({ kind: 'pending' });
    company = null;
    documents = [];
    render();

    try {
      const profile = await client.getCurrentUser();
      const participations = membershipsIn(profile.memberships, companyId);

      company = participations[0]?.company ?? null;

      const received = await client.listCompanyDocuments(companyId);

      documents = received;
      state = screenStateFor({ kind: 'done', empty: received.length === 0 });
    } catch (error) {
      company = null;
      documents = [];
      state = screenStateFor({
        kind: 'failed',
        code: error instanceof ApiFailure ? error.code : null,
      });
    }

    render();
  }

  render();

  return {
    element,

    get state(): ScreenState | null {
      return state;
    },

    get documents(): readonly Document[] {
      return documents;
    },

    load,
  };
}
